import React from 'react';
import { MapPin, ArrowRight, Clock, Navigation, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const RouteCard = ({ routeItem, onBook }) => (
    <Card
      data-testid={`route-card-${routeItem.route.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className="group lux-card rounded-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.4rem)]"
    >
      {/* Route Image */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <img 
          src={routeItem.image} 
          alt={routeItem.route}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Route Name Overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="font-display text-xl font-semibold text-white mb-1">{routeItem.route}</h3>
          <div className="flex items-center space-x-4 text-sm text-white/90">
            <span className="flex items-center">
              <Navigation className="w-4 h-4 mr-1" />
              {routeItem.distance}
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {routeItem.duration}
            </span>
          </div>
        </div>
      </div>

      <CardContent className="p-6 flex flex-col flex-1">
        <p className="text-gray-400 mb-4 leading-relaxed text-sm">
          {routeItem.description}
        </p>

        {/* Pricing - Only for packages with pricing */}
        {routeItem.pricing && (
          <div className="mb-4 p-4 bg-[#d4af37]/10 rounded-lg border border-[#d4af37]/30">
            <h4 className="text-sm font-semibold text-[#d4af37] mb-2">Package Pricing:</h4>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-lg font-bold text-[#d4af37]">SUV: ₹{routeItem.pricing.suv}</p>
                <p className="text-sm text-[#d4af37]/80">Sedan: ₹{routeItem.pricing.sedan}</p>
              </div>
              <div className="text-right text-xs text-gray-400">
                <p>All inclusive</p>
                <p className="font-semibold text-[#d4af37]">package fare</p>
              </div>
            </div>
          </div>
        )}

        {/* Full Itinerary - Only for packages with fullItinerary */}
        {routeItem.fullItinerary ? (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-200 mb-2">Complete Itinerary:</h4>
            <div className="bg-[#1a1a1a] rounded-lg p-3 max-h-48 overflow-y-auto border border-white/10">
              <ul className="space-y-1">
                {routeItem.fullItinerary.map((place, idx) => (
                  <li key={`${idx}-${place}`} className="text-xs text-gray-400 flex items-start">
                    <span className="text-[#d4af37] mr-2 font-bold">{idx + 1}.</span>
                    <span>{place}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          // Highlights for regular routes
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-200 mb-2">Highlights:</h4>
            <div className="flex flex-wrap gap-2">
              {routeItem.highlights.map((highlight) => (
                <span 
                  key={highlight}
                  className="px-3 py-1 bg-[#d4af37]/10 text-[#d4af37] text-xs font-medium rounded-full border border-[#d4af37]/20"
                >
                  {highlight}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <Button 
          onClick={onBook}
          className="w-full mt-auto rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold transition-colors"
        >
          Book This Route
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
);

export const PopularRoutes = () => {
  const routes = [
    {
      route: 'Bengaluru - Mysuru - Ooty',
      distance: '~280 km',
      duration: '6-7 hours',
      description: 'Explore the royal city of Mysuru and the scenic hill station of Ooty',
      highlights: ['Mysuru Palace', 'Ooty Lake', 'Tea Gardens'],
      image: '/route-ooty.jpeg'
    },
    {
      route: 'Bengaluru - Mysuru - Coorg',
      distance: '~270 km',
      duration: '6-7 hours',
      description: 'Visit Mysuru and discover the coffee paradise of Coorg',
      highlights: ['Abbey Falls', 'Coffee Plantations', 'Raja Seat'],
      image: '/route-coorg.jpeg'
    },
    {
      route: 'Bengaluru - Mysuru - Wayanad',
      distance: '~290 km',
      duration: '7-8 hours',
      description: 'Journey through Mysuru to the lush green valleys of Wayanad',
      highlights: ['Edakkal Caves', 'Pookode Lake', 'Wayanad Wildlife'],
      image: '/route-wayanad.jpeg'
    },
    {
      route: 'Bengaluru - Tirupati',
      distance: '~250 km',
      duration: '5-6 hours',
      description: 'Comfortable spiritual journey to the sacred Tirupati Balaji Temple',
      highlights: ['Tirumala Temple', 'Sri Venkateswara', 'Darshan'],
      image: '/route-tirupati.jpeg'
    },
    {
      route: 'Bengaluru - Dharwad',
      distance: '~420 km',
      duration: '8-9 hours',
      description: 'Long journey to North Karnataka\'s cultural and educational hub',
      highlights: ['Dharwad Pedha', 'Unkal Lake', 'Local Culture'],
      image: '/route-dharwad.jpeg'
    },
    {
      route: 'Bangalore Darshini',
      distance: 'City Tour',
      duration: 'Full Day',
      description: 'Complete Bangalore city tour covering all major temples and heritage sites',
      highlights: ['ISKON Temple', 'Vidhana Soudha', 'Tipu Palace', 'Bull Temple', 'Lalbagh', 'Cubbon Park'],
      image: '/route-bangalore.jpeg',
      pricing: { suv: 4499, sedan: 3499 },
      fullItinerary: [
        'ISKON Temple',
        'Vidhana Soudha',
        'Tipu Palace',
        'Gavigangadhareshwara Temple',
        'Bull Temple',
        'Doddaganapathi Temple',
        'Lalbagh Botanical Garden',
        'Karnataka Silk Emporium',
        'Cubbon Park',
        'Vishweshwriah Museum',
        'National Gallery of Modern Art'
      ]
    },
    {
      route: 'Adiyogi Tour',
      distance: '~120 km',
      duration: '10-12 hours',
      description: 'Spiritual journey to Isha Foundation and nearby heritage temples',
      highlights: ['Boga Nandeeshwara Temple', 'Isha Foundation', 'Adiyogi Statue', 'M. Visvesvaraya Museum'],
      image: '/route-adiyogi.jpeg',
      pricing: { suv: 5999, sedan: 4999 },
      fullItinerary: [
        'Boga Nandeeshwara Temple',
        'Kanive Basavanna Temple',
        'Sir M. Visvesvaraya Museum and Samadhi, Muddenahalli',
        'Rangasthala Ranganatha Swamy Temple',
        'Isha Foundation View',
        'Isha Foundation - Adiyogi Statue'
      ]
    }
  ];

  const outstationRoutes = routes.filter((r) => !r.pricing);
  const packageRoutes = routes.filter((r) => r.pricing);

  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="routes" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Popular Destinations
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            Popular Outstation Cab Routes from Bangalore
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Explore Karnataka and beyond with our comfortable outstation cab services
          </p>
        </div>

        {/* Routes Grid */}
        <div data-testid="outstation-routes-grid" className="flex flex-wrap justify-center gap-8">
          {outstationRoutes.map((routeItem) => (
            <RouteCard key={routeItem.route} routeItem={routeItem} onBook={scrollToBooking} />
          ))}
        </div>

        {/* Package Tours */}
        <div className="text-center mt-20 mb-12">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-3 tracking-tight">
            City &amp; Spiritual Tour Packages
          </h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Full-day sightseeing packages with fixed all-inclusive fares
          </p>
        </div>
        <div data-testid="package-tours-grid" className="flex flex-wrap justify-center gap-8">
          {packageRoutes.map((routeItem) => (
            <RouteCard key={routeItem.route} routeItem={routeItem} onBook={scrollToBooking} />
          ))}
        </div>

        {/* Pricing Info Card */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="lux-card rounded-2xl">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-display text-2xl font-semibold text-white mb-4">Outstation Pricing</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 font-medium">Sedan</span>
                      <span className="text-[#d4af37] font-bold text-xl">₹14/km</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 font-medium">SUV</span>
                      <span className="text-[#d4af37] font-bold text-xl">₹18/km</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mr-2"></div>
                      Experienced drivers
                    </li>
                    <li className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mr-2"></div>
                      Well-maintained vehicles
                    </li>
                    <li className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mr-2"></div>
                      Flexible stops allowed
                    </li>
                    <li className="flex items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mr-2"></div>
                      No hidden charges
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 text-center">
                <a href="tel:+917507867648" className="block">
                  <Button className="rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold px-6 py-6 text-base sm:text-lg w-full">
                    <Phone className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="hidden sm:inline">Call +91 75078 67648</span>
                    <span className="sm:hidden">Call Now</span>
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Custom Route CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">
            Don't see your destination? We cover all routes across South India!
          </p>
          <a href="tel:+917507867648">
            <button className="text-[#d4af37] font-semibold hover:underline">
              Contact us for custom routes →
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};
